/**
 * useMemedPrescription — carrega o script oficial da Memed com token vindo do backend,
 * espera o módulo `plataforma.prescricao` ficar pronto, pré-carrega o paciente e abre
 * o widget. Também registra o callback `onPrescriptionPrinted` para sincronizar a
 * receita emitida com o prontuário local.
 *
 * Referência: https://doc.memed.com.br/docs/frontend/mdsinapseprescricao-mdhub
 */
import { useCallback, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";

const waitForGlobal = (name, timeoutMs = 15000) =>
    new Promise((resolve, reject) => {
        const started = Date.now();
        const t = setInterval(() => {
            const value = window[name];
            if (value) {
                clearInterval(t);
                resolve(value);
            } else if (Date.now() - started > timeoutMs) {
                clearInterval(t);
                reject(new Error(`${name} não ficou disponível em ${timeoutMs}ms`));
            }
        }, 80);
    });

const injectScript = (src, token) =>
    new Promise((resolve, reject) => {
        // Se o script já está no DOM com outro token, remove — token expira/rotaciona.
        const existing = document.querySelector(`script[data-memed]`);
        if (existing && existing.dataset.token !== token) existing.remove();

        let script = document.querySelector(`script[data-memed][data-token="${token}"]`);
        if (script) return resolve(script);

        script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.dataset.memed = "true";
        script.dataset.token = token;
        script.addEventListener("load", () => resolve(script), { once: true });
        script.addEventListener(
            "error",
            () => reject(new Error("Falha ao carregar o widget da Memed")),
            { once: true }
        );
        document.head.appendChild(script);
    });

const patientToMemed = (patient) => ({
    external_id: String(patient.id),
    nome: patient.full_name,
    cpf: (patient.cpf || "").replace(/\D/g, ""),
    ...(patient.phone && { telefone: patient.phone.replace(/\D/g, "") }),
    ...(patient.email && { email: patient.email }),
    ...(patient.city_uf && { cidade: patient.city_uf }),
    ...(patient.address && { endereco: patient.address }),
});

export default function useMemedPrescription({ patient, consultationId, onEmitted } = {}) {
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState("idle"); // idle | ready | error
    const [error, setError] = useState(null);
    const initialised = useRef(false);
    const printedHandler = useRef(null);

    const open = useCallback(async () => {
        if (!patient) return;
        setLoading(true);
        setError(null);
        try {
            // 1) Buscar token curto do backend (não cachear — rotaciona)
            const { data } = await api.get("/memed/doctor-token");
            const { token, script_url } = data;

            // 2) Injeta o script oficial com data-token
            await injectScript(script_url, token);

            // 3) Aguarda os globals — MdSinapsePrescricao e MdHub
            const sinapse = await waitForGlobal("MdSinapsePrescricao");
            await waitForGlobal("MdHub");

            // 4) Registra o listener ANTES do setPaciente/show (uma única vez por sessão)
            if (!printedHandler.current) {
                printedHandler.current = async (payload) => {
                    try {
                        const memedId =
                            payload?.prescricao?.id ||
                            payload?.prescription?.id ||
                            payload?.id ||
                            `memed-${Date.now()}`;
                        const url =
                            payload?.prescricao?.link ||
                            payload?.prescricao?.url ||
                            payload?.link ||
                            payload?.url ||
                            null;
                        const summary =
                            payload?.prescricao?.medicamentos_prescritos ||
                            payload?.medications ||
                            null;
                        await api.post("/memed/prescriptions/register", {
                            patient_id: patient.id,
                            consultation_id: consultationId || null,
                            memed_prescription_id: String(memedId),
                            prescription_url: url,
                            summary: typeof summary === "string" ? summary : JSON.stringify(summary || {}),
                        });
                        onEmitted?.(payload);
                    } catch (e) {
                        // Best-effort — não bloqueia o médico
                        console.error("[Memed] falha ao registrar receita local:", e);
                    }
                };
                sinapse.event.add("onPrescriptionPrinted", printedHandler.current);
            }

            // 5) Aguarda a inicialização do módulo e injeta o paciente
            //    Ordem CORRETA (per Memed docs): registrar listener PRIMEIRO,
            //    depois tentar chamada direta como fallback. NUNCA chamar
            //    setPaciente/show antes do core:moduleInit.
            const openWhenReady = () =>
                new Promise((resolve, reject) => {
                    let done = false;
                    const finish = (ok, err) => {
                        if (done) return;
                        done = true;
                        clearTimeout(timeout);
                        ok ? resolve(true) : reject(err);
                    };
                    const timeout = setTimeout(
                        () => finish(false, new Error("Timeout aguardando o módulo Memed inicializar")),
                        15000
                    );

                    const setAndShow = () => {
                        try {
                            window.MdHub.command.send(
                                "plataforma.prescricao",
                                "setPaciente",
                                patientToMemed(patient)
                            );
                            // Pequena espera pro widget processar setPaciente antes de renderizar
                            setTimeout(() => {
                                try {
                                    window.MdHub.module.show("plataforma.prescricao");
                                    finish(true);
                                } catch (e) {
                                    finish(false, e);
                                }
                            }, 200);
                        } catch (e) {
                            finish(false, e);
                        }
                    };

                    // 1) Sempre registra o listener primeiro
                    sinapse.event.add("core:moduleInit", (module) => {
                        if (module?.name === "plataforma.prescricao") setAndShow();
                    });

                    // 2) Fallback: se o módulo JÁ estava inicializado (2ª abertura na mesma sessão),
                    //    o evento não vai disparar. Verifica após 500ms se dá pra chamar direto.
                    setTimeout(() => {
                        if (done) return;
                        const available = window.MdHub?.module?.isModuleAvailable;
                        if (typeof available === "function" && available("plataforma.prescricao")) {
                            setAndShow();
                        } else if (window.MdHub?.module && !available) {
                            // SDK antigo sem isModuleAvailable — tenta chamada direta
                            setAndShow();
                        }
                    }, 800);
                });

            await openWhenReady();
            initialised.current = true;
            setStatus("ready");
        } catch (e) {
            const msg = formatApiError(e) || e.message || "Erro ao abrir Memed";
            setError(msg);
            setStatus("error");
        } finally {
            setLoading(false);
        }
    }, [patient, consultationId, onEmitted]);

    return { open, loading, status, error };
}
